"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Calendar, Eye, MessageCircle, TrendingUp, ExternalLink, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import type { PreviewImageDto, CategorySimpleDto } from "@/lib/services/productService";
import { StatusTag, STATUS_TAG_CONFIG } from "./product-types";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ProductHoverPreviewProps {
  product: {
    id: string;
    name: string;
    tagline: string;
    description?: string;
    logoUrl: string;
    previewImages?: PreviewImageDto[];
    screenshots?: string[]; // Deprecated: use previewImages instead
    websiteUrl?: string;
    voteCount: number;
    viewCount: number;
    commentCount: number;
    launchDate?: Date;
    categories: CategorySimpleDto[];
    highlights?: string[];
    statusTags?: StatusTag[];
  };
  className?: string;
}

/**
 * Format large numbers with K/M suffixes
 */
const formatCount = (count: number): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toLocaleString("ru-RU");
};

/**
 * Format date to Russian locale
 */
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ProductHoverPreview - Steam-style hover preview for products
 *
 * Features:
 * - Screenshot carousel with autoplay
 * - Detailed product information
 * - Key highlights
 * - Engagement metrics
 * - Smooth animations
 * - Mobile-optimized (disabled on small screens)
 *
 * @example
 * ```tsx
 * <HoverCard openDelay={300} closeDelay={100}>
 *   <HoverCardTrigger>Product Card</HoverCardTrigger>
 *   <HoverCardContent>
 *     <ProductHoverPreview product={productData} />
 *   </HoverCardContent>
 * </HoverCard>
 * ```
 */
export function ProductHoverPreview({
  product,
  className,
}: ProductHoverPreviewProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Screenshot carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      duration: 20,
    },
    [Autoplay({ delay: 3000, stopOnInteraction: false })]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Use previewImages if available, fallback to screenshots for backward compatibility
  const previewImages = product.previewImages || [];
  const screenshots = previewImages.length > 0
    ? previewImages.map(img => img.imageUrl)
    : (product.screenshots || []);
  const hasScreenshots = screenshots.length > 0;

  return (
    <div
      className={cn(
        "w-[420px] max-w-[90vw] overflow-hidden rounded-2xl",
        "bg-card border border-border shadow-2xl",
        className
      )}
    >
      {/* Screenshot Carousel or Description Fallback */}
      {hasScreenshots ? (
        <div className="relative bg-background-elevated">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {screenshots.map((screenshot, index) => (
                <div
                  key={index}
                  className="relative min-w-0 flex-[0_0_100%]"
                  style={{ aspectRatio: "16/9" }}
                >
                  <Image
                    src={screenshot}
                    alt={`${product.name} screenshot ${index + 1}`}
                    fill
                    className="object-cover"
                    quality={90}
                    sizes="420px"
                    priority={index === 0}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        product.name
                      )}&background=FF6154&color=fff&size=800`;
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Dots */}
          {screenshots.length > 1 && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 px-4">
              {screenshots.map((_, index) => (
                <button
                  key={index}
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={cn(
                    "h-1.5 rounded-full transition-all duration-300",
                    index === selectedIndex
                      ? "w-6 bg-primary"
                      : "w-1.5 bg-foreground-tertiary/50 hover:bg-foreground-tertiary"
                  )}
                  aria-label={`Go to screenshot ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* Product Info */}
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-xl overflow-hidden">
              <Image
                src={product.logoUrl}
                alt={product.name}
                width={48}
                height={48}
                className="object-contain"
                quality={90}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    product.name
                  )}&background=FF6154&color=fff`;
                }}
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground text-base leading-tight mb-1">
              {product.name}
            </h3>
            <p className="text-sm text-foreground-secondary line-clamp-2">
              {product.tagline}
            </p>
          </div>
        </div>

        {/* Description fallback when no preview images */}
        {!hasScreenshots && product.description && (
          <p className="text-sm text-foreground-secondary line-clamp-4 leading-relaxed">
            {product.description}
          </p>
        )}

        {/* Status Tags */}
        {product.statusTags && product.statusTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {product.statusTags.slice(0, 3).map((tag) => {
              const config = STATUS_TAG_CONFIG[tag];
              return (
                <span
                  key={tag}
                  className={cn(
                    "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs",
                    "border border-border transition-colors",
                    config.color,
                    config.bgColor
                  )}
                >
                  <span role="img" aria-hidden="true">
                    {config.icon}
                  </span>
                  <span>{config.label}</span>
                </span>
              );
            })}
            {product.statusTags.length > 3 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                +{product.statusTags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Highlights */}
        {product.highlights && product.highlights.length > 0 && (
          <div className="space-y-1.5">
            {product.highlights.slice(0, 3).map((highlight, index) => (
              <div
                key={index}
                className="flex items-start gap-2 text-sm text-foreground-secondary"
              >
                <Sparkles className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
                <span className="line-clamp-1">{highlight}</span>
              </div>
            ))}
          </div>
        )}

        {/* Categories */}
        {product.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {product.categories.slice(0, 4).map((category) => (
              <span
                key={category.id}
                className="text-xs px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground"
              >
                {category.name}
              </span>
            ))}
            {product.categories.length > 4 && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                +{product.categories.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {/* Votes */}
          <div className="flex items-center gap-2 text-foreground-secondary">
            <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-primary-subtle">
              <TrendingUp className="h-4 w-4 text-primary-text" />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-foreground">
                {formatCount(product.voteCount)}
              </span>
              <span className="text-xs text-foreground-tertiary">голосов</span>
            </div>
          </div>

          {/* Views */}
          <div className="flex items-center gap-2 text-foreground-secondary">
            <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-muted">
              <Eye className="h-4 w-4 text-foreground-secondary" />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-foreground">
                {formatCount(product.viewCount)}
              </span>
              <span className="text-xs text-foreground-tertiary">
                просмотров
              </span>
            </div>
          </div>

          {/* Comments */}
          <div className="flex items-center gap-2 text-foreground-secondary">
            <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-muted">
              <MessageCircle className="h-4 w-4 text-foreground-secondary" />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-foreground">
                {product.commentCount}
              </span>
              <span className="text-xs text-foreground-tertiary">
                комментариев
              </span>
            </div>
          </div>

          {/* Launch Date */}
          {product.launchDate && (
            <div className="flex items-center gap-2 text-foreground-secondary">
              <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-muted">
                <Calendar className="h-4 w-4 text-foreground-secondary" />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-foreground text-xs">
                  {formatDate(product.launchDate).split(" ")[0]}{" "}
                  {formatDate(product.launchDate).split(" ")[1]}
                </span>
                <span className="text-xs text-foreground-tertiary">запуск</span>
              </div>
            </div>
          )}
        </div>

        {/* Visit Website CTA */}
        {product.websiteUrl && (
          <a
            href={product.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-full",
              "bg-primary text-primary-foreground font-medium text-sm",
              "hover:bg-primary-hover transition-colors",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <span>Открыть сайт</span>
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}
